/* Shared error reporter v1. Keep the copies in panel/web/mobile in sync. */
function safePath(value) {
  if (typeof value !== 'string') return '';
  let path = value.slice(0, 2048);
  try {
    if (/^https?:\/\//i.test(path)) path = new URL(path).pathname;
  } catch {
    return '';
  }
  return path
    .split(/[?#]/, 1)[0]
    .replace(/[^/]+/g, (part) =>
      /@|%40/i.test(part) || part.length > 60 || /^\d{7,}$/.test(part) ? ':redacted' : part,
    )
    .slice(0, 240);
}
function safeText(value, limit = 500) {
  if (typeof value !== 'string') return '';
  return value
    .slice(0, 16000)
    .replace(/https?:\/\/[^\s<>"']+/gi, (url) => safePath(url))
    .replace(/(?:Bearer|Basic)\s+[A-Za-z0-9+/_=.-]+/gi, '[credential]')
    .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g, '[token]')
    .replace(
      /(["']?(?:password|passwordHash|passwd|secret|token|accessToken|refreshToken|authorization|cookie|otp|verificationCode|cardNumber|pan|cvv|cvc|email|phone|mobile)["']?\s*[:=]\s*)(?:"[^"]*"|'[^']*'|[^\s,;}\]]+)/gi,
      '$1[redacted]',
    )
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, '[email]')
    .replace(/(?:\+?\d[\d ()-]{5,}\d)/g, '[number]')
    .replace(/\b[A-Za-z0-9+/_=-]{40,}\b/g, '[opaque]')
    .replace(
      /(?:^|\n)[^\n]*(?:INSERT INTO|UPDATE\s+\w+\s+SET|SELECT\s+.+\s+FROM|(?:requestBody|payload|postParams|bankResponse)\s*[:=])[^\n]*/gi,
      '\n[details omitted]',
    )
    .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, '')
    .slice(0, limit);
}
function safeStack(value) {
  return typeof value === 'string'
    ? safeText(
        value
          .split('\n')
          .filter((line) => /^\s*(?:at\s|[A-Za-z0-9_$.[\]<>/ -]*@)/.test(line))
          .slice(0, 18)
          .join('\n'),
        4000,
      )
    : '';
}
function safeCode(value, fallback = '') {
  return typeof value === 'string' && /^[a-zA-Z][a-zA-Z0-9_./:-]{0,79}$/.test(value)
    ? value
    : fallback;
}
function eventId() {
  if (globalThis.crypto && typeof globalThis.crypto.randomUUID === 'function')
    return globalThis.crypto.randomUUID();
  return 'err_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 14);
}

function jsonBytes(value) {
  let bytes = 0;
  for (const point of JSON.stringify(value)) {
    const code = point.codePointAt(0);
    bytes += code <= 0x7f ? 1 : code <= 0x7ff ? 2 : code <= 0xffff ? 3 : 4;
  }
  return bytes;
}

function createErrorReporter(config) {
  const pending = new Map();
  const captures = new Set();
  const groups = new Map();
  let generation = 0;
  let timer;
  let inFlight;
  let dropped = 0;
  const enabled = config.enabled !== false;
  const identity = () => {
    try {
      const token = config.getToken ? config.getToken() : null;
      if (!token || typeof token.then !== 'function') return Promise.resolve(token);
      return new Promise((resolve) => {
        const timeout = setTimeout(() => resolve(null), 500);
        Promise.resolve(token).then(
          (value) => {
            clearTimeout(timeout);
            resolve(value);
          },
          () => {
            clearTimeout(timeout);
            resolve(null);
          },
        );
      });
    } catch {
      return Promise.resolve(null);
    }
  };
  function capture(error, options = {}) {
    if (captures.size >= 40) {
      dropped++;
      return;
    }
    if (!enabled) return;
    try {
      if (error?.stale || error?.name === 'AbortError') return;
      const status = Number(error?.status);
      if (status >= 400 && status < 500) return;
      const epoch = generation;
      const now = Date.now();
      const route = safePath(options.route ?? (config.getRoute ? config.getRoute() : ''));
      const message = safeText(
        error?.message ?? (typeof error === 'string' ? error : 'Unknown error'),
      );
      const stack = safeStack(error?.stack);
      const code = safeCode(options.code ?? error?.code, 'CLIENT_ERROR');
      const operation = safeCode(options.operation);
      const version = safeCode(config.version, 'unknown');
      const requestId = /^[a-f0-9-]{36}$/i.test(options.requestId ?? '')
        ? options.requestId
        : undefined;
      const tokenPromise = options.identity ?? identity();
      const task = Promise.resolve(tokenPromise)
        .then((token) => {
          if (epoch !== generation) return;
          // Identity stays outside the transmitted diagnostic payload.
          const key = JSON.stringify([
            token,
            code,
            message,
            stack,
            route,
            operation,
            Math.floor(now / 60000),
          ]);
          let group = groups.get(key);
          if (!group) {
            if (groups.size >= 100) groups.delete(groups.keys().next().value);
            group = { id: eventId(), count: 0 };
            groups.set(key, group);
          }
          group.count = Math.min(10000, group.count + 1);
          if (!pending.has(group.id) && pending.size >= 40) {
            dropped++;
            return;
          }
          pending.set(group.id, {
            token,
            attempts: 0,
            queuedAt: now,
            event: {
              id: group.id,
              platform: config.platform,
              version,
              message,
              stack,
              code,
              route,
              operation,
              requestId,
              occurredAt: now,
              occurrences: group.count,
            },
          });
        })
        .catch(() => {
          dropped++;
        })
        .finally(() => {
          captures.delete(task);
        });
      captures.add(task);
    } catch {
      dropped++;
    }
  }
  function reset() {
    generation++;
    pending.clear();
    groups.clear();
  }
  function flush() {
    if (!enabled) return Promise.resolve();
    if (inFlight) return inFlight;
    const epoch = generation;
    inFlight = (async () => {
      await Promise.allSettled([...captures]);
      if (epoch !== generation) return;
      const now = Date.now();
      for (const [id, entry] of pending) {
        if (now - entry.queuedAt > 60000) {
          pending.delete(id);
          dropped++;
        }
      }
      const first = pending.values().next().value;
      if (!first) return;
      const batch = [];
      for (const entry of pending.values()) {
        if (entry.token !== first.token) continue;
        if (
          batch.length === 10 ||
          jsonBytes({ errors: [...batch.map((item) => item.event), entry.event] }) > 48 * 1024
        )
          break;
        batch.push(entry);
      }
      batch.forEach((entry) => pending.delete(entry.event.id));
      try {
        const accepted = await config.send(
          batch.map((entry) => entry.event),
          first.token,
        );
        if (!accepted) throw new Error('Report not accepted');
      } catch {
        if (epoch !== generation) return;
        for (const entry of batch) {
          entry.attempts++;
          if (entry.attempts < 2 && pending.size < 40 && !pending.has(entry.event.id))
            pending.set(entry.event.id, entry);
          else dropped++;
        }
      }
    })().finally(() => {
      inFlight = undefined;
    });
    return inFlight;
  }
  return {
    capture,
    flush,
    reset,
    identity,
    start() {
      if (enabled && !timer) {
        timer = setInterval(() => {
          void flush();
        }, 5000);
        if (timer && typeof timer.unref === 'function') timer.unref();
      }
    },
    stop() {
      if (timer) clearInterval(timer);
      timer = undefined;
    },
    get state() {
      return { pending: pending.size, dropped };
    },
  };
}

function watchApiFetch(target, apiUrl, reporter) {
  if (!target || typeof target.fetch !== 'function' || !apiUrl) return () => {};
  const original = target.fetch;
  let base;
  try {
    base = new URL(apiUrl, target.location?.origin);
  } catch {
    return () => {};
  }
  const basePath = base.pathname.replace(/\/$/, '');
  const wrapped = function (input, init) {
    let url;
    try {
      url = new URL(
        typeof input === 'string' ? input : (input?.url ?? String(input)),
        target.location?.origin ?? base.origin,
      );
    } catch {
      return original.call(target, input, init);
    }
    if (
      url.origin !== base.origin ||
      !(url.pathname === basePath || url.pathname.startsWith(basePath + '/')) ||
      url.pathname.slice(basePath.length).startsWith('/system-errors')
    )
      return original.call(target, input, init);
    const identity = reporter.identity();
    const route = safePath(url.pathname);
    const operation = safeCode(init?.method ?? input?.method, 'GET');
    return original.call(target, input, init).then(
      (response) => {
        if (response.status >= 500)
          reporter.capture(new Error('API request failed (' + response.status + ')'), {
            code: 'HTTP_' + response.status,
            route,
            operation,
            identity,
            requestId: response.headers?.get?.('X-Request-ID'),
          });
        return response;
      },
      (error) => {
        if (error?.name !== 'AbortError' && !init?.signal?.aborted && !input?.signal?.aborted)
          reporter.capture(new Error('API connection failed'), {
            code: 'NETWORK_ERROR',
            route,
            operation,
            identity,
          });
        throw error;
      },
    );
  };
  target.fetch = wrapped;
  return () => {
    if (target.fetch === wrapped) target.fetch = original;
  };
}

function installBrowserErrors(target, reporter) {
  if (!target || typeof target.addEventListener !== 'function') return () => {};
  const onError = (event) => {
    if (event.error) reporter.capture(event.error, { code: 'UNHANDLED_ERROR' });
  };
  const onRejection = (event) => reporter.capture(event.reason, { code: 'UNHANDLED_REJECTION' });
  const onHidden = () => {
    if (target.document?.visibilityState === 'hidden') void reporter.flush();
  };
  target.addEventListener('error', onError);
  target.addEventListener('unhandledrejection', onRejection);
  target.document?.addEventListener('visibilitychange', onHidden);
  reporter.start();
  return () => {
    target.removeEventListener('error', onError);
    target.removeEventListener('unhandledrejection', onRejection);
    target.document?.removeEventListener('visibilitychange', onHidden);
    reporter.stop();
  };
}
module.exports = {
  createErrorReporter,
  watchApiFetch,
  installBrowserErrors,
  safeText,
  safeStack,
  safePath,
};
